import { LibraryExercise, CustomExercise, MuscleGroup, Equipment } from './index';
import { Language } from './translations';
import { LocalizedExerciseTitles } from './workout/shared';
import { removeAccents } from './utils';
import { intersectionWith } from 'lodash';

interface SearchableLibraryExercise extends LibraryExercise {
  localized_muscle_group_name: string;
}

interface SearchableCustomExercise extends CustomExercise {
  localized_muscle_group_name: string;
}

export type SearchableExerciseTemplate = SearchableLibraryExercise | SearchableCustomExercise;

/** The fields the exercise search reads. */
export interface SearchableExercise extends LocalizedExerciseTitles {
  title: string;
  is_custom: boolean;
  priority: number;
  muscle_group: MuscleGroup;
  equipment_category: Equipment;
  localized_muscle_group_name: string;
  aka?: string;
  manual_tag?: string;
}

interface FilterExercisesProps<T extends SearchableExercise> {
  exercises: T[];
  muscleGroups: MuscleGroup[];
  equipment: Equipment[];
  searchText: string;
  language: Language;
}

export const getExerciseMatches = <T extends SearchableExercise>({
  exercises,
  muscleGroups,
  equipment,
  searchText,
  language,
}: FilterExercisesProps<T>): ExerciseSearchMatch<T>[] => {
  let result = exercises;

  result = filterExercisesWithMuscleGroup(muscleGroups, result);
  result = filterExercisesWithEquipment(equipment, result);

  const normalizedSearchText = normalizeSearchText(searchText, language);

  let matches = filterExercisesWithSearchText(normalizedSearchText, result, language);
  matches = orderExercisesWithSearchText(normalizedSearchText, matches);

  return matches;
};

const filterExercisesWithMuscleGroup = <T extends SearchableExercise>(
  muscleGroup: MuscleGroup[],
  exercises: T[],
): T[] => {
  if (muscleGroup.length === 0) {
    return exercises;
  }

  return exercises.filter(rd => muscleGroup.includes(rd.muscle_group));
};

const filterExercisesWithEquipment = <T extends SearchableExercise>(
  equipmentType: Equipment[],
  exercises: T[],
): T[] => {
  if (equipmentType.length === 0) {
    return exercises;
  }

  return exercises.filter(rd => equipmentType.includes(rd.equipment_category));
};

export type ExerciseSearchMatch<T> = {
  exercise: T;
  matchType: 'title' | 'muscle_group' | 'other' | 'custom';
};

// Old filter exercise functionality, will be removed when new feature goes live
const filterExercisesWithSearchText = <T extends SearchableExercise>(
  searchText: string,
  exercises: T[],
  language: Language,
): ExerciseSearchMatch<T>[] => {
  if (searchText.length === 0) {
    return exercises.map(e => ({ exercise: e, matchType: 'other' }));
  }

  const matches: ExerciseSearchMatch<T>[] = [];

  for (const e of exercises) {
    const searchableSearchText = searchable(searchText);
    const searchableEnglishTitle = searchable(e.title);

    const searchableLocalizedTitleMap = !e.is_custom
      ? {
          en: searchable(e.title),
          es: e.es_title ? searchable(e.es_title) : undefined,
          de: e.de_title ? searchable(e.de_title) : undefined,
          fr: e.fr_title ? searchable(e.fr_title) : undefined,
          it: e.it_title ? searchable(e.it_title) : undefined,
          pt_PT: e.pt_title ? searchable(e.pt_title) : undefined,
          tr: e.tr_title ? searchable(e.tr_title) : undefined,
          zh_CN: e.zh_cn_title ? searchable(e.zh_cn_title) : undefined,
          zh_TW: e.zh_tw_title ? searchable(e.zh_tw_title) : undefined,
          ru: e.ru_title ? searchable(e.ru_title) : undefined,
          ja: e.ja_title ? searchable(e.ja_title) : undefined,
          ko: e.ko_title ? searchable(e.ko_title) : undefined,
          ar: e.ar_title ? searchable(e.ar_title) : undefined,
          ca: e.ca_title ? searchable(e.ca_title) : undefined,
          hi: e.hi_title ? searchable(e.hi_title) : undefined,
          pl: e.pl_title ? searchable(e.pl_title) : undefined,
          pt_BR: e.pt_br_title ? searchable(e.pt_br_title) : undefined,
        }
      : {
          en: searchable(e.title),
        };

    const searchableLocalizedTitle =
      searchableLocalizedTitleMap[language] || searchableLocalizedTitleMap.en;

    const searchableLocalizedMuscleGroupName = searchable(e.localized_muscle_group_name);
    const searchableMuscleGroup = searchable(e.muscle_group);
    const searchableMisspelledDumbbellEquipment =
      e.equipment_category === 'dumbbell' ? 'dumbell' : '';
    const searchableTags = !e.is_custom && e.manual_tag ? searchable(e.manual_tag) : '';
    const searchableAka = !e.is_custom && e.aka ? searchable(e.aka) : '';

    if (
      searchableLocalizedTitle.includes(searchableSearchText) ||
      searchableEnglishTitle.includes(searchableSearchText)
    ) {
      matches.push({ exercise: e, matchType: 'title' });
      continue;
    } else if (
      searchableLocalizedMuscleGroupName.includes(searchableSearchText) ||
      searchableMuscleGroup.includes(searchableSearchText)
    ) {
      matches.push({ exercise: e, matchType: 'muscle_group' });
      continue;
    }

    if (e.is_custom && 'custom'.includes(searchableSearchText)) {
      matches.push({ exercise: e, matchType: 'custom' });
      continue;
    }

    const splitSearch = searchableSearchText.split(' ');
    const splitTitle = [
      searchableLocalizedTitle,
      searchableEnglishTitle,
      searchableTags,
      searchableAka,
      searchableMisspelledDumbbellEquipment,
    ]
      .join(' ')
      .split(' ');

    // Find:
    // title ['arnold', 'press', '(barbell)']
    // search ['press', 'bar']
    if (
      intersectionWith(splitSearch, splitTitle, (search, title) => title.includes(search))
        .length === splitSearch.length
    ) {
      matches.push({ exercise: e, matchType: 'other' });
      continue;
    }
  }
  return matches;
};

const orderExercisesWithSearchText = <T extends SearchableExercise>(
  searchText: string,
  exerciseMatches: ExerciseSearchMatch<T>[],
): ExerciseSearchMatch<T>[] => {
  if (searchText.length === 0) {
    return exerciseMatches;
  }

  return exerciseMatches
    .reduce((accu: any[], currentMatch) => {
      const lowercaseTitle = currentMatch.exercise.title.toLowerCase();

      if (lowercaseTitle.startsWith(searchText)) {
        accu.push({
          exercise: currentMatch,
          weight: currentMatch.exercise.priority * 2,
        });
        return accu;
      }

      accu.push({
        exercise: currentMatch,
        weight: currentMatch.exercise.priority,
      });
      return accu;
    }, [])
    .sort((a, b) => b.weight - a.weight)
    .map(a => a.exercise);
};

/**
 * trims, lowercases and removes trailing "s" character to
 * simplify english searches
 */
const normalizeSearchText = (searchText: string, language: Language) => {
  const searchableSearchText = searchable(searchText).replace('-', ' ').trim();

  if (language !== 'en') return searchableSearchText;
  if (searchableSearchText.length < 3) return searchableSearchText;

  const lastCharacter = searchableSearchText.slice(-1);
  if (lastCharacter !== 's') return searchableSearchText;

  return searchableSearchText.slice(0, -1);
};

const searchable = (str: string): string => {
  return removeAccents(str.toLowerCase());
};
/**
 * Exercise templates that are shown at the top of the Exercise Library to
 * users who don't have any workouts logged.
 */
export const popularExerciseTemplateIds = [
  'B8127AD1', // Lying Leg Curl (Machine)"
  '11A123F3', // Seated Leg Curl (Machine)"
  'D04AC939', // Squat (Barbell)"
  '75A4F6C4', // Leg Extension (Machine)"
  '422B08F1', // Lateral Raise (Dumbbell)"
  'C7973E0E', // Leg Press (Machine)"
  '07B38369', // Incline Bench Press (Dumbbell)"
  '3601968B', // Bench Press (Dumbbell)"
  '6A6C31A5', // Lat Pulldown (Cable)"
  '0393F233', // Seated Cable Row - V Grip (Cable)"
  '37FCC2BB', // Bicep Curl (Dumbbell)"
  '55E6546F', // Bent Over Row (Barbell)"
  '94B7239B', // Triceps Rope Pushdown"
  'C6272009', // Deadlift (Barbell)"
  '878CD1D0', // Shoulder Press (Dumbbell)"
  '651F844C', // Cable Fly Crossovers"
  '79D0BB3A', // Bench Press (Barbell)"
  '93A552C6', // Triceps Pushdown"
  '7E3BC8B6', // Hammer Curl (Dumbbell)"
  'BE640BA0', // Face Pull
] as const;
